/* eslint-disable @angular-eslint/no-output-on-prefix */
import {
  Component,
  EventEmitter,
  Injectable,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { getLocaleMonthNames, getLocaleDayNames, FormStyle, TranslationWidth } from '@angular/common';
import { AbstractControl, ControlContainer, FormGroupDirective } from '@angular/forms';
import { NgbDatepickerI18n, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { LanguageService } from 'src/app/shared/foundation/i18n/language.service';
import { SettingsService } from 'src/app/shared/foundation/settings/settings.service';
import {
  DatePickerHelper,
  DatePickerListener,
} from 'src/app/client/repositories/lib/common/utils/date-picker.helper';

@Injectable()
class DynamicDatepickerI18n extends NgbDatepickerI18n {
  locale = 'de';

  getWeekdayLabel(weekday: number): string {
    const days = getLocaleDayNames(this.locale, FormStyle.Standalone, TranslationWidth.Abbreviated);
    // Angular weekdays start at Sunday (0), ng-bootstrap at Monday (1)
    return days[(weekday % 7)];
  }

  getMonthShortName(month: number): string {
    return getLocaleMonthNames(this.locale, FormStyle.Standalone, TranslationWidth.Abbreviated)[month - 1];
  }

  getMonthFullName(month: number): string {
    return getLocaleMonthNames(this.locale, FormStyle.Standalone, TranslationWidth.Wide)[month - 1];
  }

  getDayAriaLabel(date: NgbDateStruct): string {
    return `${date.day}.${date.month}.${date.year}`;
  }
}

@Component({
  selector: 'app-datepicker',
  templateUrl: './datepicker.component.html',
  styleUrls: ['./datepicker.component.scss'],
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
  providers: [
    { provide: NgbDatepickerI18n, useClass: DynamicDatepickerI18n },
  ],
})
export class DatepickerComponent
  implements OnInit, DatePickerListener, OnChanges, OnDestroy
{
  @Input() value?: Date | string | null;
  @Input() valid?: boolean;
  @Input() controlName?: string;
  @Input() maxDate?: Date;
  @Input() minDate?: Date;
  @Input() noDays = false;
  @Input() readonly = true;

  @Output() onDateChange: EventEmitter<Date> = new EventEmitter<Date>();
  public datePickerHelper?: DatePickerHelper;

  ngbMinDate?: NgbDateStruct;
  ngbMaxDate?: NgbDateStruct;
  startDate?: NgbDateStruct;
  displayValue = '';

  private langSub?: Subscription;

  constructor(
    private formGroupDirective: FormGroupDirective,
    private languageService: LanguageService,
    private i18n: NgbDatepickerI18n,
    private settingsService: SettingsService
  ) {}

  ngOnInit(): void {
    (this.i18n as DynamicDatepickerI18n).locale = this.settingsService.getLocaleId() || 'de';
    this.setDatePickerHelper();
    this.syncDates();
    this.langSub = this.languageService.getLanguage().subscribe((lang) => {
      (this.i18n as DynamicDatepickerI18n).locale = lang;
    });
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  ngOnChanges(): void {
    if (this.datePickerHelper) {
      this.setDatePickerHelper();
    }
    this.syncDates();
  }

  private syncDates(): void {
    if (this.isValidDate(this.value)) {
      this.displayValue = this.formatDate(this.value);
      this.startDate = this.toNgbDate(this.value);
    } else {
      this.displayValue = typeof this.value === 'string' ? this.value : '';
      this.startDate = undefined;
    }
    this.ngbMinDate = this.minDate ? this.toNgbDate(this.minDate) : undefined;
    this.ngbMaxDate = this.maxDate ? this.toNgbDate(this.maxDate) : undefined;
  }

  onDateSelect(ngbDate: NgbDateStruct): void {
    const date = new Date(ngbDate.year, ngbDate.month - 1, ngbDate.day);
    this.displayValue = this.formatDate(date);

    this.setControlValue(date);

    this.datePickerHelper?.setDate(date);
  }

  onDateInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    this.displayValue = value;

    if (!value.trim()) {
      this.setControlValue(null);
      this.onDateChange.emit(null!);
      return;
    }

    const date = this.parseDate(value);
    if (date) {
      this.setControlValue(date);
      this.datePickerHelper?.setDate(date);
      return;
    }

    this.setControlValue(value);
    this.onDateChange.emit(null!);
  }

  onDateBlur(): void {
    this.getControl()?.markAsTouched();

    const date = this.parseDate(this.displayValue);
    if (date) {
      this.displayValue = this.formatDate(date);
    }
  }

  public onDateChanged(newDate: Date, helper: DatePickerHelper) {
    if (!newDate?.getDate()) {
      this.onDateChange.emit(null!);
    } else {
      this.onDateChange.emit(newDate);
    }
  }

  setDatePickerHelper(): void {
    this.datePickerHelper = new DatePickerHelper(this, null!);
  }

  private setControlValue(value: Date | string | null): void {
    const control = this.getControl();
    if (!control) {
      return;
    }

    control.setValue(value);
    control.markAsDirty();
    control.updateValueAndValidity();
  }

  private getControl(): AbstractControl | null {
    if (!this.controlName) {
      return null;
    }

    return this.formGroupDirective.form.get(this.controlName);
  }

  private parseDate(value: string): Date | null {
    const trimmedValue = value.trim();
    const isoMatch = trimmedValue.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    const localMatch = trimmedValue.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);

    if (isoMatch) {
      return this.createDate(
        Number(isoMatch[1]),
        Number(isoMatch[2]),
        Number(isoMatch[3])
      );
    }

    if (localMatch) {
      return this.createDate(
        Number(localMatch[3]),
        Number(localMatch[2]),
        Number(localMatch[1])
      );
    }

    return null;
  }

  private createDate(year: number, month: number, day: number): Date | null {
    const date = new Date(year, month - 1, day);

    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return null;
    }

    return date;
  }

  private formatDate(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}.${month}.${date.getFullYear()}`;
  }

  private toNgbDate(date: Date): NgbDateStruct {
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
    };
  }

  private isValidDate(value: unknown): value is Date {
    return value instanceof Date && !Number.isNaN(value.getTime());
  }
}
